import type { Market } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Divider,
  Drawer,
  Spread,
  Stack,
  Text,
  TextLinkButton,
  Hidden,
  Badge,
} from 'braid-design-system';
import { useState } from 'react';

import translations from './.vocab/index.ts';
import * as styles from './Filters.css.ts';
import { CompanyFilter } from './components/CompanyFilter/CompanyFilter.tsx';
import { LocationFilter } from './components/LocationFilter/LocationFilter.tsx';
import { RoleTitleFilter } from './components/RoleTitleFilter/RoleTitleFilter.tsx';
import { useResetFiltersUponMarketChangeEffect } from './hooks/useResetFiltersUponMarketChangeEffect.ts';
import type { Suggestion } from './types.ts';

type Props = {
  disabled?: boolean;
  filterItemsCount: number;
  roleTitleState: {
    selectedItems: Suggestion[];
    setSelectedItems: (newState: Suggestion[]) => void;
    matchLatestRoleTitle: boolean;
    setMatchLatestRoleTitle: (newState: boolean) => void;
  };
  locationState: {
    selectedItems: Suggestion[];
    setSelectedItems: (newState: Suggestion[]) => void;
  };
  companyState: {
    selectedItems: Suggestion[];
    setSelectedItems: (newState: Suggestion[]) => void;
    matchLatestCompany: boolean;
    setMatchLatestCompany: (newState: boolean) => void;
  };
  // add more filter states here
  mobilePanelState: {
    isOpen: boolean;
    setIsOpen: (openState: false) => false | void;
  };
  onFilterChanged?: ({
    roleTitleSelectedItems,
    locationSelectedItems,
    matchLatestRoleTitle,
    companySelectedItems,
    matchLatestCompany,
  }: {
    roleTitleSelectedItems: Suggestion[];
    locationSelectedItems: Suggestion[];
    matchLatestRoleTitle: boolean;
    companySelectedItems: Suggestion[];
    matchLatestCompany: boolean;
  }) => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onClearAll: () => void;
  marketState: {
    value: Market;
    onChange: (marketSelected: Market) => void;
  };
};

export const Filters = ({
  disabled = false,
  filterItemsCount,
  roleTitleState,
  locationState,
  companyState,
  mobilePanelState: { isOpen: isDrawerOpen, setIsOpen: setIsDrawerOpen },
  onFilterChanged,
  onClick,
  onClearAll,
  marketState,
}: Props) => {
  const { t } = useTranslations(translations);

  const [isRoleTitleExpanded, setIsRoleTitleExpanded] = useState(true);
  const [isLocationExpanded, setIsLocationExpanded] = useState(true);
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(true);

  const allCurrentStates = {
    roleTitleSelectedItems: roleTitleState.selectedItems,
    locationSelectedItems: locationState.selectedItems,
    matchLatestRoleTitle: roleTitleState.matchLatestRoleTitle,
    companySelectedItems: companyState.selectedItems,
    matchLatestCompany: companyState.matchLatestCompany,
  };

  const handleRoleTitleOnChange = (newSelectedItems: Suggestion[]) => {
    roleTitleState.setSelectedItems(newSelectedItems);
    onFilterChanged?.({
      ...allCurrentStates,
      roleTitleSelectedItems: newSelectedItems,
    });
  };

  const handleMatchLatestRoleTitleChange = (newState: boolean) => {
    roleTitleState.setMatchLatestRoleTitle(newState);
    onFilterChanged?.({
      ...allCurrentStates,
      matchLatestRoleTitle: newState,
    });
  };

  const handleLocationOnChange = (newSelectedItems: Suggestion[]) => {
    locationState.setSelectedItems(newSelectedItems);
    onFilterChanged?.({
      ...allCurrentStates,
      locationSelectedItems: newSelectedItems,
    });
  };

  const handleCompanyOnChange = (newSelectedItems: Suggestion[]) => {
    companyState.setSelectedItems(newSelectedItems);
    onFilterChanged?.({
      ...allCurrentStates,
      companySelectedItems: newSelectedItems,
    });
  };

  const handleMatchLatestCompanyChange = (newState: boolean) => {
    companyState.setMatchLatestCompany(newState);
    onFilterChanged?.({
      ...allCurrentStates,
      matchLatestCompany: newState,
    });
  };

  const handleClearAll = () => {
    onClearAll();
    onFilterChanged?.({
      roleTitleSelectedItems: [],
      locationSelectedItems: [],
      matchLatestRoleTitle: false,
      companySelectedItems: [],
      matchLatestCompany: false,
    });
  };

  useResetFiltersUponMarketChangeEffect({
    market: marketState.value,
    reset: handleClearAll,
  });

  const renderHeader = () => (
    <Spread space="small">
      <Text weight="strong">
        {t('FILTERS')}
        {filterItemsCount > 0 && (
          <>
            &nbsp;&nbsp;
            <Badge tone="info" weight="strong">
              {filterItemsCount}
            </Badge>
          </>
        )}
      </Text>

      {filterItemsCount > 0 && (
        <Text>
          <TextLinkButton weight="weak" onClick={handleClearAll}>
            {t('CLEAR_ALL')}
          </TextLinkButton>
        </Text>
      )}
    </Spread>
  );

  const renderFilters = () => (
    <Stack space="xlarge">
      <Box marginTop="xxsmall">
        <RoleTitleFilter
          expanded={isRoleTitleExpanded}
          selectedMarket={marketState.value}
          selectedItems={roleTitleState.selectedItems}
          onRoleTitleChange={handleRoleTitleOnChange}
          onExpandedToggle={setIsRoleTitleExpanded}
          matchLatestRoleTitle={roleTitleState.matchLatestRoleTitle}
          onMatchLatestRoleTitleChange={handleMatchLatestRoleTitleChange}
        />
      </Box>
      <LocationFilter
        expanded={isLocationExpanded}
        selectedItems={locationState.selectedItems}
        onChange={handleLocationOnChange}
        onExpandedToggle={setIsLocationExpanded}
        marketState={marketState}
      />
      <Box>
        <CompanyFilter
          expanded={isCompanyExpanded}
          selectedMarket={marketState.value}
          selectedItems={companyState.selectedItems}
          onCompanyChange={handleCompanyOnChange}
          onExpandedToggle={setIsCompanyExpanded}
          matchLatestCompany={companyState.matchLatestCompany}
          onMatchLatestCompanyChange={handleMatchLatestCompanyChange}
        />
      </Box>
      {/* Add more filters here */}
    </Stack>
  );

  return (
    <Box boxShadow="small" borderRadius="large">
      <Drawer
        id="filters-modile"
        open={!disabled && isDrawerOpen}
        // @ts-expect-error hacky way as title only accepts string now
        title={<Box marginTop="xxsmall">{renderHeader()}</Box>}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Divider />
        {renderFilters()}
      </Drawer>
      <Hidden below="tablet">
        <Box
          position="relative"
          borderRadius="large"
          background="body"
          padding="medium"
          className={styles.filtersAside}
          onClick={onClick}
        >
          {disabled && <Overlay />}
          <Stack space="medium">
            {renderHeader()}
            <Divider />
            {renderFilters()}
          </Stack>
        </Box>
      </Hidden>
    </Box>
  );
};

const Overlay = () => (
  <Box
    data={{ testid: 'filters-overlay' }}
    width="full"
    height="full"
    position="absolute"
    top={0}
    left={0}
    zIndex="modal"
  />
);
